import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/layout/v3/Logo';
import { cardShell } from '@/components/dashboard-v2/tokens';
import { Home, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ErrorPageProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  showRetryButton?: boolean;
  onRetry?: () => void;
}

// Brand buttons: square corners, action blue for the main one, a white
// outlined one beside it. Never red — an error screen stays calm.
const PRIMARY_BUTTON =
  'h-11 gap-2 rounded-[4px] bg-[#1d2fb5] px-5 text-[14px] font-semibold tracking-[-0.01em] text-white hover:bg-[#1728a0] focus-visible:ring-[#1d2fb5]';
const SECONDARY_BUTTON =
  'h-11 gap-2 rounded-[4px] border-[#c9d2e3] bg-white px-5 text-[14px] font-semibold tracking-[-0.01em] text-[#001830] hover:border-[#1d2fb5] hover:bg-white hover:text-[#1d2fb5] focus-visible:ring-[#1d2fb5]';

export function ErrorPage({
  title = 'Something went wrong',
  message = 'We apologise for the inconvenience. Please try refreshing the page or return home.',
  showHomeButton = true,
  showRetryButton = true,
  onRetry,
}: ErrorPageProps) {
  const navigate = useNavigate();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleHome = () => {
    navigate('/');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#eef1f7] px-4 py-10 font-body text-[#001830] antialiased">
      <div className={cn(cardShell, 'w-full max-w-[480px] px-6 py-8 sm:px-10 sm:py-10')}>
        {/* Brand mark — this screen replaces the whole app, header included. */}
        <div className="flex items-center gap-2 text-[#000b57]">
          <Logo size={20} />
          <span className="text-[15px] font-bold tracking-tight">socialyse.ai</span>
        </div>

        <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.05em] text-[#1d2fb5]">Error</p>
        <h1 className="mt-1.5 font-display text-[28px] font-normal leading-[1.2] text-[#001830] sm:text-[30px]">
          {title}
        </h1>
        <p className="mt-3 text-[15px] leading-[1.55] text-[#475068]">{message}</p>

        {(showRetryButton || showHomeButton) && (
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            {showRetryButton && (
              <Button onClick={handleRetry} className={PRIMARY_BUTTON}>
                <RefreshCw className="h-4 w-4" aria-hidden />
                Refresh page
              </Button>
            )}
            {showHomeButton && (
              <Button
                onClick={handleHome}
                variant={showRetryButton ? 'outline' : 'default'}
                className={showRetryButton ? SECONDARY_BUTTON : PRIMARY_BUTTON}
              >
                <Home className="h-4 w-4" aria-hidden />
                Go home
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 404 Not Found page
 */
export function NotFoundPage() {
  return (
    <ErrorPage
      title="Page not found"
      message="The page you're looking for doesn't exist or has been moved."
      showRetryButton={false}
    />
  );
}
